<template>
    <div>
        <v-tabs v-model="tab" color="primary" v-if="scheduleTable.selectedWallType">
            <v-tab prepend-icon="mdi-square" :text="$t('components.panelCountEstimator.wallComposition')" value="Composition"></v-tab>
            <v-tab prepend-icon="mdi-cloud" :text="$t('components.panelCountEstimator.carbonValue')" value="CarbonFootprint"></v-tab>
        </v-tabs>
        <v-tabs-window v-model="tab">
            <v-tabs-window-item value="Composition">
                <v-card class="prefab-detail-card" elevation="3">
                    <v-row>
                        <v-col cols="6">
                            <v-row class="prefab-detail-row-content" v-for="(name, index) in wallDescription.display_name" :key="index">
                                {{ name }}
                            </v-row>
                        </v-col>
                        <v-col cols="6">
                            <v-img :src="imageSrc" class="mx-auto" max-height="300"></v-img>
                        </v-col>
                    </v-row>
                </v-card>
            </v-tabs-window-item>
            <v-tabs-window-item value="CarbonFootprint">
                <v-card class="prefab-detail-card" elevation="3">
                    <v-row>
                        <v-col cols="4">
                          <p v-html="`${$t('components.panelCountEstimator.carbonFootprint')} (${$t('measuringUnits.kg')} ${$t('measuringUnits.carbonDioxide').toUpperCase()}/${$t('measuringUnits.kg')}): ${wallDescription.carbon_footprint_kg_co2_kg}`"></p>
                          <p v-html="`${$t('components.panelCountEstimator.carbonFootprint')} (${$t('measuringUnits.meters')}${$t('cube')} ${$t('measuringUnits.carbonDioxide').toUpperCase()}/${$t('measuringUnits.kg')} ${$t('measuringUnits.meters')}${$t('cube')}): ${wallDescription.carbon_footprint_m3_co2kg_m3}`"></p>
                          <p v-html="`${$t('components.panelCountEstimator.density')} (${$t('measuringUnits.kg')}/${$t('measuringUnits.meters')}${$t('cube')}): ${wallDescription.density_kg_m3}`"></p>
                          <p v-html="`${$t('components.panelCountEstimator.materialThickness')} (${$t('measuringUnits.meters')}): ${wallDescription.material_thickness_m}`"></p>
                          <p v-html="`${$t('components.panelCountEstimator.materialVolume')}: ${wallDescription.material_volume}`"></p>
                        </v-col>
                        <v-col cols="8">
                            <apexchart width="500" type="donut" :options="options" :series="series"></apexchart>
                        </v-col>
                    </v-row>
                </v-card>
            </v-tabs-window-item>
        </v-tabs-window>
    </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';

const props = defineProps({
    tab: String,
    scheduleTable: Object
});

const options = computed(() => {
  return { labels: props.scheduleTable.walls[props.scheduleTable.selectedWallType]?.map(wall => wall.display_name) ?? [] };
});

const series = computed(() => {
  return props.scheduleTable.walls[props.scheduleTable.selectedWallType]?.map(wall => Number(wall.carbon_footprint_m3_co2kg_m3)) ?? [];
});

const imageSrc = computed(() => {
  if (!props.scheduleTable.selectedWallType) return '';
  const selectedWallType = props.scheduleTable.selectedWallType.replace(/\s+/g, '');
  return require(`@/assets/Legend_${selectedWallType}.jpg`);
});

const wallDescription = computed(() => {
  if (!props.scheduleTable.selectedWallType) return {};

  const selectedWallType = props.scheduleTable.selectedWallType;
  const walls = props.scheduleTable.walls[selectedWallType];

  const totalValues = walls.reduce((acc, wall) => {
    acc.carbon_footprint_kg_co2_kg += Number(wall.carbon_footprint_kg_co2_kg);
    acc.carbon_footprint_m3_co2kg_m3 += Number(wall.carbon_footprint_m3_co2kg_m3);
    acc.density_kg_m3 += Number(wall.density_kg_m3);
    acc.material_thickness_m += Number(wall.material_thickness_m);
    acc.material_volume += Number(wall.material_volume);
    return acc;
  }, {
    carbon_footprint_kg_co2_kg: 0,
    carbon_footprint_m3_co2kg_m3: 0,
    density_kg_m3: 0,
    material_thickness_m: 0,
    material_volume: 0
  });

  return {
    wallType: selectedWallType,
    display_name: walls.map(wall => wall.display_name),
    carbon_footprint_kg_co2_kg: totalValues.carbon_footprint_kg_co2_kg.toFixed(4),
    carbon_footprint_m3_co2kg_m3: totalValues.carbon_footprint_m3_co2kg_m3.toFixed(2),
    density_kg_m3: totalValues.density_kg_m3.toFixed(2),
    material_thickness_m: totalValues.material_thickness_m.toFixed(3),
    material_volume: totalValues.material_volume.toFixed(2)
  };
});

const emit = defineEmits(['update:tab']);

const tab = ref(props.tab);

watch(tab, (val) => emit('update:tab', val));
</script>

<style scoped>
@import '@/assets/styles/components/panelCountEstimator/PrefabWallDetailTab.css';
</style>